"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.SponsorFieldByConfig = void 0;
var _mui = require("@ori-ui/mui");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _lodash = _interopRequireDefault(require("lodash.camelcase"));
var _lodash2 = _interopRequireDefault(require("lodash.split"));
var _react = require("react");
var _reactHookForm = require("react-hook-form");
var _Localization = require("../../../Localization");
var _constants = require("../../../constants");
var _contexts = require("../../../contexts");
var _models = require("../../../models");
var _utils = require("../../../utils");
var _SharedStyledComponents = require("../../SharedStyledComponents");
var _jsxRuntime = require("react/jsx-runtime");
const StyledLink = (0, _mui.styled)('a')(({
  theme
}) => ({
  color: theme.palette.error.main,
  textDecoration: 'underline',
  fontWeight: 'bold',
  '&:hover': {
    color: theme.palette.error.main,
    textDecoration: 'underline'
  }
}));
const InputTitle = (0, _mui.styled)(_mui.Typography)(({
  theme
}) => ({
  marginBottom: theme.spacing(3),
  fontWeight: 'bold'
}));
const handleChangeAllowOnlyNumber = (value, allowOnlyNumbers) => {
  return allowOnlyNumbers ? Number(value) : value;
};
const SponsorFieldByConfig = props => {
  const {
    name,
    label,
    helperText,
    isEditable,
    isAvailable,
    displayOrder,
    validation,
    initialValue,
    isAddressMatchRequired,
    keyboardType,
    inputType,
    customPlaceholder,
    enableLowercaseLetters,
    useUpperCase,
    handleOnFocus,
    handleOnBlur,
    sameParentalValues,
    allowOnlyNumbers = true
  } = props;
  const {
    errorData
  } = (0, _contexts.useFormError)();
  const {
    customErrors
  } = errorData;
  const {
    loginUrl
  } = (0, _contexts.useConfiguration)();
  const {
    setFocus,
    setValue,
    setError,
    clearErrors
  } = (0, _reactHookForm.useFormContext)();
  const {
    getTestIdProps,
    getTestId
  } = (0, _testidGenerator.default)();
  const submitError = customErrors?.find(error => error.fieldName?.toLowerCase() === name.toLowerCase());
  const formatError = (0, _lodash2.default)(submitError?.fieldName, '.').map(item => (0, _lodash.default)(item));
  const joinFormatError = formatError.join('.');
  const isSponsorField = name === 'sponsorDetails.sponsorNumber.value';
  (0, _react.useEffect)(() => {
    if (submitError && !isSponsorField) {
      setFocus(joinFormatError);
    }
  }, [customErrors, joinFormatError, setFocus, submitError, isSponsorField]);
  const errorMessage = (0, _utils.getErrorLocalizationByCode)(submitError?.errorCode, name);
  const regexPattern = validation?.regularExpression ? new RegExp(validation.regularExpression) : undefined;
  const readOnly = isAddressMatchRequired ?? false;
  const fieldPlaceholder = customPlaceholder;
  const capitalize = enableLowercaseLetters ? 'off' : 'on';
  const readOnlyStyleBox = {
    display: 'flex',
    height: '0',
    alignItems: 'center',
    justifyContent: 'center'
  };
  const handleTrimValue = fieldValue => {
    const correctValue = useUpperCase ? fieldValue?.toLocaleUpperCase() : fieldValue;
    setValue(name, correctValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
    handleOnBlur?.();
  };
  (0, _react.useEffect)(() => {
    if (sameParentalValues) {
      setError(name, {
        message: _constants.defaultTranslations.ParentalConsentSameEmailValidation
      });
    } else {
      clearErrors(name);
    }
  }, [clearErrors, name, sameParentalValues, setError]);

  // we need start tabindex from 1 but displayOrder starts from 0
  const correctTabIndex = typeof displayOrder === 'number' ? displayOrder + 1 : displayOrder;
  const renderField = () => /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Grid, {
    size: {
      xs: 12,
      sm: 12
    },
    style: {
      order: displayOrder
    },
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_mui.Box, {
      margin: readOnly ? '0' : '0 auto 20px',
      maxWidth: 500,
      style: readOnly ? readOnlyStyleBox : undefined,
      children: [isSponsorField ? /*#__PURE__*/(0, _jsxRuntime.jsx)(InputTitle, {
        variant: "subtitle1",
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          defaultMessage: _constants.defaultTranslations.PersonalDetails_YourSponsor,
          id: _models.TranslationKey.PersonalDetails_YourSponsor
        })
      }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactHookForm.Controller, {
        name: name,
        rules: {
          required: validation?.isMandatory,
          maxLength: validation?.maxLength,
          minLength: validation?.minLength,
          pattern: regexPattern,
          validate: () => {
            if (sameParentalValues) {
              return _constants.defaultTranslations.ParentalConsentSameEmailValidation;
            }
            return true;
          }
        },
        defaultValue: initialValue,
        render: ({
          field: {
            onChange,
            ...field
          },
          fieldState
        }) => /*#__PURE__*/(0, _jsxRuntime.jsxs)(_mui.FormControl, {
          fullWidth: true,
          children: [readOnly ? null : /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.TextField, {
            fullWidth: true,
            type: inputType ?? 'text',
            label: label,
            placeholder: fieldPlaceholder,
            variant: "outlined",
            error: !!fieldState.error || Boolean(submitError),
            helperText: fieldState.error ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_SharedStyledComponents.ErrorMesssageText, {
              ...getTestIdProps(`${name}-text-field-error-message`),
              children: (0, _utils.renderValidationMessage)(fieldState.error)
            }) : submitError ? null : helperText,
            disabled: !isEditable,
            required: validation?.isMandatory,
            defaultValue: field.value,
            inputProps: {
              tabIndex: correctTabIndex,
              maxLength: validation?.maxLength,
              minLength: validation?.minLength ?? undefined,
              inputMode: keyboardType ?? 'text',
              'data-testid': getTestId(`${name}-text-field`),
              autoCapitalize: capitalize,
              style: {
                textTransform: useUpperCase ? 'uppercase' : 'initial'
              },
              sx: {
                '&::placeholder': {
                  textTransform: 'initial'
                }
              }
            }
            // eslint-disable-next-line react/jsx-no-duplicate-props --- we needed is not duplicate props
            ,
            InputProps: {
              endAdornment: fieldState.error ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_SharedStyledComponents.StyledErrorCircle, {
                color: "error"
              }) : null,
              autoComplete: 'off'
            },
            ...field,
            value: field.value || '',
            onBlur: () => handleTrimValue(field.value),
            onFocus: () => handleOnFocus?.(),
            onChange: e => onChange(handleChangeAllowOnlyNumber(e.target.value, allowOnlyNumbers))
          }), submitError ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.FormHelperText, {
            error: true,
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
              ...errorMessage,
              values: {
                loginLink: /*#__PURE__*/(0, _jsxRuntime.jsx)(StyledLink, {
                  href: loginUrl,
                  target: "_blank",
                  rel: "noreferrer",
                  children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
                    defaultMessage: _constants.defaultTranslations.loginLinkLabel,
                    id: _models.TranslationKey.loginLinkLabel
                  })
                })
              }
            })
          }) : null]
        })
      })]
    })
  });
  const fieldComponent = isAvailable && renderField();
  return fieldComponent;
};
exports.SponsorFieldByConfig = SponsorFieldByConfig;
//# sourceMappingURL=SponsorFieldByConfig.js.map